import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import {
  Trash2,
  Clock,
  AlertCircle,
  CheckCircle,
  Pause,
  FileText,
  ChevronDown,
  Flag,
  MessageSquare,
  MoreVertical,
  Loader2,
  Building2,
  Calendar,
  Map
} from 'lucide-react';

interface SavedWorkCardProps {
  job: {
    id: string;
    business_name: string;
    client_name?: string;
    law_firm?: string;
    bank_name?: string;
    status: string;
    workflow_status?: string;
    auto_status?: string;
    priority?: string;
    notes?: string;
    saved_at?: string;
    created_at?: string;
    updated_at?: string;
    days_inactive?: number;
    doc_count?: number;
    total_transactions?: number;
    needs_review?: number;
    step?: number;
    source?: string;
    stats?: {
      total_transactions?: number;
      auto_approved?: number;
      needs_review?: number;
    };
  };
  onDelete: (id: string) => void;
  onStatusChange?: () => void;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  draft: { 
    label: 'Draft', 
    color: 'text-gray-600', 
    bgColor: 'bg-gray-100',
    icon: <FileText className="w-3 h-3" />
  },
  awaiting_data: { 
    label: 'Awaiting Data', 
    color: 'text-amber-600', 
    bgColor: 'bg-amber-50',
    icon: <Clock className="w-3 h-3" />
  },
  processing: { 
    label: 'Processing', 
    color: 'text-blue-600', 
    bgColor: 'bg-blue-50',
    icon: <Loader2 className="w-3 h-3 animate-spin" />
  },
  needs_review: { 
    label: 'Needs Review', 
    color: 'text-orange-600', 
    bgColor: 'bg-orange-50',
    icon: <AlertCircle className="w-3 h-3" />
  },
  ready: { 
    label: 'Ready', 
    color: 'text-green-600', 
    bgColor: 'bg-green-50',
    icon: <CheckCircle className="w-3 h-3" />
  },
  stalled: { 
    label: 'Stalled', 
    color: 'text-red-600', 
    bgColor: 'bg-red-50',
    icon: <Pause className="w-3 h-3" />
  },
  on_hold: { 
    label: 'On Hold', 
    color: 'text-purple-600', 
    bgColor: 'bg-purple-50',
    icon: <Pause className="w-3 h-3" />
  },
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string; border: string }> = {
  low: { label: 'Low', color: 'text-gray-500', border: 'border-l-gray-300' },
  medium: { label: 'Medium', color: 'text-blue-500', border: 'border-l-blue-400' },
  high: { label: 'High', color: 'text-orange-500', border: 'border-l-orange-400' },
  urgent: { label: 'Urgent', color: 'text-red-500', border: 'border-l-red-500' },
};

export default function SavedWorkCard({ job, onDelete, onStatusChange }: SavedWorkCardProps) {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [localStatus, setLocalStatus] = useState(job.workflow_status || job.auto_status || 'draft');
  const [localPriority, setLocalPriority] = useState(job.priority || 'medium');

  const statusConfig = STATUS_CONFIG[localStatus] || STATUS_CONFIG.draft;
  const priorityConfig = PRIORITY_CONFIG[localPriority] || PRIORITY_CONFIG.medium;

  // Generate short ID from UUID (first 8 chars)
  const shortId = `#${job.id.substring(0, 8).toUpperCase()}`;

  // Calculate progress based on step (wizard has 7 steps)
  const TOTAL_WIZARD_STEPS = 7;
  const progressPercent = job.step ? Math.min((job.step / TOTAL_WIZARD_STEPS) * 100, 100) : 0;

  // Transaction stats
  const totalTxn = job.total_transactions || job.stats?.total_transactions || 0;
  const needsReview = job.needs_review || job.stats?.needs_review || 0;
  const autoApproved = totalTxn - needsReview;

  const handleContinue = async () => {
    if (job.source === 'wizard') {
      try {
        const token = await getToken();
        const draftData = await api.get(`/api/v1/reports/drafts/${job.id}`, {
          headers: { Authorization: `Bearer ${token}` },
          showErrorToast: false
        });
        
        localStorage.removeItem('wizard_state');
        localStorage.removeItem('wizard_backup');
        localStorage.setItem('current_draft_id', job.id);
        
        navigate('/wizard-v2', {
          state: {
            freshDraftData: draftData.data || {},
            freshDraftStep: draftData.step || 3,
            draftId: job.id,
            fromDashboard: true
          }
        });
      } catch (err) {
        console.error('Error fetching draft:', err);
        navigate('/wizard-v2');
      }
    } else {
      navigate(`/cleanup/validation/${job.id}`);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    setIsUpdating(true);
    try {
      const token = await getToken();
      await api.patch(`/api/v1/reports/drafts/${job.id}/status`, 
        { workflow_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLocalStatus(newStatus);
      onStatusChange?.();
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setIsUpdating(false);
      setShowStatusDropdown(false);
    }
  };

  const handlePriorityChange = async (newPriority: string) => {
    setIsUpdating(true);
    try {
      const token = await getToken();
      await api.patch(`/api/v1/reports/drafts/${job.id}/status`, 
        { priority: newPriority },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLocalPriority(newPriority);
      onStatusChange?.();
    } catch (err) {
      console.error('Error updating priority:', err);
    } finally {
      setIsUpdating(false);
      setShowPriorityDropdown(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysAgoText = () => {
    if (job.days_inactive === undefined) return null;
    if (job.days_inactive === 0) return 'Today';
    if (job.days_inactive === 1) return '1 day ago';
    return `${job.days_inactive} days ago`;
  };

  return (
    <div 
      className={`bg-white rounded-lg border-l-4 ${priorityConfig.border} border border-gray-200 p-4 hover:shadow-md transition-all relative`}
      data-testid={`saved-work-card-${job.id}`}
    >
      {/* Top Row: ID, Name, Law Firm, Status */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Short ID */}
            <span className="text-xs font-mono text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">
              {shortId}
            </span>
            
            {/* Business Name */}
            <h4 className="font-semibold text-gray-900 truncate">
              {job.client_name || job.business_name || 'Unnamed Claim'}
            </h4>
            
            {/* Source Badge */}
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
              {job.bank_name || 'Claim Wizard'}
            </span>
          </div>
          
          {/* Law Firm */}
          {job.law_firm && (
            <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-600">
              <Building2 className="w-3.5 h-3.5 text-gray-400" />
              <span>{job.law_firm}</span>
            </div>
          )}
        </div>

        {/* Status Badge with Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color} hover:opacity-80 transition-opacity`}
            data-testid={`status-badge-${job.id}`}
          >
            {statusConfig.icon}
            <span>{statusConfig.label}</span>
            <ChevronDown className="w-3 h-3" />
          </button>
          
          {showStatusDropdown && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[160px]">
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => handleStatusChange(key)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                    localStatus === key ? 'bg-gray-50 font-medium' : ''
                  }`}
                >
                  <span className={config.color}>{config.icon}</span>
                  <span>{config.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>Step {job.step || 1} of {TOTAL_WIZARD_STEPS}</span>
          <span>{Math.round(progressPercent)}% complete</span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Stats Row */}
      <div className="flex items-center gap-4 text-sm text-gray-600 mb-3 flex-wrap">
        <span className="flex items-center gap-1">
          <FileText className="w-3.5 h-3.5 text-gray-400" />
          {job.doc_count || 0} docs
        </span>
        <span>•</span>
        <span>{totalTxn.toLocaleString()} transactions</span>
        <span>•</span>
        <span className="text-green-600">{autoApproved.toLocaleString()} approved</span>
        {needsReview > 0 && (
          <>
            <span>•</span>
            <span className="text-amber-600 font-medium">{needsReview.toLocaleString()} need review</span>
          </>
        )}
      </div>

      {/* Bottom Row: Date, Priority, Actions */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <div className="flex items-center gap-4 text-xs text-gray-500">
          {/* Last Activity */}
          <div className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            <span>Updated {formatDate(job.updated_at || job.saved_at)}</span>
            {job.days_inactive !== undefined && job.days_inactive > 0 && (
              <span className={`ml-1 ${job.days_inactive >= 7 ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                ({getDaysAgoText()})
              </span>
            )}
          </div>
          
          {/* Priority Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
              className={`flex items-center gap-1 px-2 py-0.5 rounded hover:bg-gray-100 ${priorityConfig.color}`}
              data-testid={`priority-btn-${job.id}`}
            >
              <Flag className="w-3 h-3" />
              <span className="capitalize">{localPriority}</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            
            {showPriorityDropdown && (
              <div className="absolute left-0 bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[100px]">
                {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => handlePriorityChange(key)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                      localPriority === key ? 'bg-gray-50 font-medium' : ''
                    } ${config.color}`}
                  >
                    <Flag className="w-3 h-3" />
                    <span className="capitalize">{key}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/roadmap/${job.id}`)}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="View Roadmap"
            data-testid={`draft-roadmap-${job.id}`}
          >
            <Map className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(job.id)}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete draft"
            data-testid={`draft-delete-${job.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            onClick={handleContinue}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            data-testid={`draft-continue-${job.id}`}
          >
            Continue
          </button>
        </div>
      </div>

      {/* Click outside handler for dropdowns */}
      {(showStatusDropdown || showPriorityDropdown) && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => {
            setShowStatusDropdown(false);
            setShowPriorityDropdown(false);
          }}
        />
      )}
      
      {/* Loading overlay */}
      {isUpdating && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded-lg">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
        </div>
      )}
    </div>
  );
}
